import { apiUrl } from "@/lib/env";
import { captureEvent } from "./analytics";
import { getClickIds } from "./attribution";

// The one waitlist code path on the landing: a bare email POSTed to the public
// worker endpoint. Every capture surface (the marketing WaitlistForm, and the
// same form embedded in the hero composer's waitlist modal) funnels through
// here so there is a single request/response contract to reason about.
// The endpoint returns 200 with `status: "exists"` for a duplicate email and
// `status: "success"` otherwise; any non-2xx is treated as an error.
export type WaitlistOutcome = "success" | "exists" | "error";

export type WaitlistResult = {
  outcome: WaitlistOutcome;
  /** Server-provided message, when present — callers prefer their own i18n copy. */
  message: string;
  /** The user's personal referral code, when the server generated one. */
  referralCode?: string;
  /** The user's personal referral link (marketing origin + ?ref=code). */
  referralLink?: string;
};

// Matches a single email token (no surrounding whitespace or stray `@`), so it
// can be pulled OUT of a free-form chat sentence — not just tested against a
// bare field value.
const EMAIL_PATTERN = /[^\s@]+@[^\s@]+\.[^\s@]+/;
// Sentence punctuation the greedy token match can sweep up on the tail
// ("...reach me at me@x.com." → "me@x.com").
const TRAILING_PUNCT = /[.,;:!?)\]}>'"]+$/;

/**
 * Pull the first email address out of arbitrary text (a chat message like
 * "my email is trader@example.com"), or return null when there is none. The
 * server is the source of truth on validity — this just isolates a clean token
 * so we never POST a whole sentence to the waitlist endpoint (which would fail
 * its `z.email()` check).
 */
export function extractEmail(value: string): string | null {
  const match = value.match(EMAIL_PATTERN);
  if (!match) return null;
  const email = match[0].replace(TRAILING_PUNCT, "");
  // A stripped tail can leave a token with no dot after the `@` — reject it.
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : null;
}

export function isEmail(value: string): boolean {
  return extractEmail(value) !== null;
}

// Flaky-transit tolerance. Visitors on filtered/lossy networks (mainland-China
// webviews especially) can load the page yet have the next connection attempt
// blackholed — e.g. Chromium racing advertised HTTP/3 over UDP that never
// answers. Each attempt gets a hard timeout, and a failed attempt is retried
// on a fresh connection (by which point the browser has usually marked the
// broken transport and falls back to TCP). Non-2xx responses are NOT retried —
// the server answered. A lost-response retry is safe: duplicates return 200.
const SUBMIT_ATTEMPTS = 3;
const SUBMIT_TIMEOUT_MS = 8000;
const SUBMIT_RETRY_DELAY_MS = 800;

function attemptTimeout(): AbortSignal | undefined {
  return typeof AbortSignal !== "undefined" && "timeout" in AbortSignal
    ? AbortSignal.timeout(SUBMIT_TIMEOUT_MS)
    : undefined;
}

async function postWithRetry(url: string, body: string): Promise<Response> {
  let lastError: unknown;
  for (let attempt = 0; attempt < SUBMIT_ATTEMPTS; attempt++) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, SUBMIT_RETRY_DELAY_MS));
    }
    try {
      return await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        signal: attemptTimeout(),
      });
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError;
}

export async function submitWaitlist(
  email: string,
  referralCode?: string,
): Promise<WaitlistResult> {
  const result = await postWaitlist(email, referralCode);
  // The one conversion event that matters on the landing. Fired from the single
  // funnel so every entry point (the WaitlistForm and the hero composer) is
  // counted once, with its outcome. No email/PII — just the outcome + whether a
  // referral code rode along. Pairs with `waitlist_started` (WaitlistForm) for
  // a start -> submit drop-off funnel.
  captureEvent("waitlist_submitted", {
    outcome: result.outcome,
    has_referral: Boolean(referralCode),
  });
  return result;
}

async function postWaitlist(email: string, referralCode?: string): Promise<WaitlistResult> {
  try {
    const payload: Record<string, unknown> = { email: email.trim() };
    if (referralCode) payload.referralCode = referralCode;
    // Attach any captured ad-click ids so the server can attribute the
    // conversion. Absent for organic visitors — the key is simply omitted.
    const clickIds = getClickIds();
    if (clickIds) payload.clickIds = clickIds;

    const resp = await postWithRetry(apiUrl("/api/v1/public/waitlist"), JSON.stringify(payload));
    const data = (await resp.json().catch(() => ({}))) as {
      status?: string;
      message?: string;
      referralCode?: string;
      referralLink?: string;
    };
    if (resp.ok) {
      return {
        outcome: data.status === "exists" ? "exists" : "success",
        message: data.message ?? "",
        referralCode: data.referralCode,
        referralLink: data.referralLink,
      };
    }
    return { outcome: "error", message: data.message ?? "" };
  } catch {
    return { outcome: "error", message: "" };
  }
}
